import {gql} from '@apollo/client';
import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {DataPolicyCollaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getSelectOwnerHelpMessage, OwnershipType} from 'create-components/OwnableUtil';
import SelectOwnerField from 'create-components/SelectOwnerField';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {refetchSavedSearchSettings} from 'search/saved-searches/SavedSearchesQuery';
import EntityStore from 'stores/EntityStore';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';

import {SavedSearchSettingsModalQuery} from './__generated/SavedSearchSettingsModalQuery';
import SavedSearchSharingPane from './SavedSearchSharingPane';
import {getOwnerInfo, SavedSearchPermissionLevel, setDataPolicyId} from './SavedSearchSharingUtil';

type SavedSearchSettingsModalQuery_savedSearch_SavedSearchNode = ExtractByTypename<
  SavedSearchSettingsModalQuery['savedSearch'],
  'SavedSearchNode'
>;

/** Fields that can be modified using the SavedSearchSettingsModal. */
export interface SavedSearchSettingsModalModifiableFields {
  name: string;
  description: string;
  ownerJson: Owner;
  ownerInfoJson: OwnerInfo;
  collaboratorsJson: Array<DataPolicyCollaborator>;
}

export interface SavedSearchSettingsModalProps {
  savedSearchId?: string;
  initialValues?: {name: string; description: string};
  title: string;
  /** Called when submitting changes. If rejected, the modal won't close, allowing the user to try again. */
  onRequestSubmit: (values: SavedSearchSettingsModalModifiableFields) => Promise<void>;
  onRequestClose: () => void;
  /** Return false to cancel deletion. */
  onDelete?: () => Promise<boolean>;
  /** Which text box to focus when opening the modal. */
  autoFocus?: 'name' | 'share';
  noWriteAccess?: boolean;
  /** True if submitting will make a copy of the search, making the current user the owner. */
  copy?: boolean;
}

interface PropsFromQuery {
  existingSavedSearch?: SavedSearchSettingsModalQuery['savedSearch'];
  // Saved searches only support 3 predefined data policies, unlike other ownables.
  // Policy IDs will be undefined on tenants where they haven't been initalized yet.
  // TODO(BNCH-54344): Make prop required after all tenants have been initialized.
  dataPolicyIds?: {[level in SavedSearchPermissionLevel]: string};
  loading?: boolean;
}

// In addition to the sharing info, refetch name and description since they could be stale (the user may leave
// the sidepanel open for a long time before deciding to edit the search)
export const SAVED_SEARCH_SETTINGS_MODAL_QUERY = gql`
  query SavedSearchSettingsModalQuery($savedSearchId: StrictString!)
  @skipAudit
  @engComponent(name: SIGHTGLASS) {
    savedSearch(id: $savedSearchId) {
      ... on SavedSearchNode {
        id
        name
        description
        ownerJson
        ownerInfoJson
        collaboratorsJson
        permissions {
          ...SavedSearchPermissionsGraphQL
        }
      }
    }

    savedSearchDataPolicies {
      readPolicy {
        ... on DataPolicyNode {
          id
        }
      }
      editPolicy {
        ... on DataPolicyNode {
          id
        }
      }
      adminPolicy {
        ... on DataPolicyNode {
          id
        }
      }
    }
  }
  ${getFragmentWrapper('SavedSearchPermissions').fragment}
`;

export default function QueryingSavedSearchSettingsModal(props: SavedSearchSettingsModalProps): JSX.Element {
  function getPolicyIds({
    readPolicy,
    editPolicy,
    adminPolicy,
  }: SavedSearchSettingsModalQuery['savedSearchDataPolicies']): PropsFromQuery['dataPolicyIds'] {
    if ([readPolicy, editPolicy, adminPolicy].some((policy) => isInaccessible(policy))) {
      return undefined;
    }
    return {
      [SavedSearchPermissionLevel.VIEW]: (readPolicy as {id: string}).id,
      [SavedSearchPermissionLevel.EDIT]: (editPolicy as {id: string}).id,
      [SavedSearchPermissionLevel.ADMIN]: (adminPolicy as {id: string}).id,
    };
  }

  return (
    <BenchlingQuery<SavedSearchSettingsModalQuery, {savedSearchId: string}>
      query={SAVED_SEARCH_SETTINGS_MODAL_QUERY}
      skip={props.savedSearchId == null}
      variables={{savedSearchId: props.savedSearchId!}}
      renderLoading={() => <SavedSearchSettingsModal {...props} loading={true} />}
      fetchPolicy="cache-and-network"
      renderCachedDataWhenRefetching={true}
    >
      {(results: SavedSearchSettingsModalQuery | undefined) => (
        <SavedSearchSettingsModal
          {...props}
          existingSavedSearch={results?.savedSearch}
          dataPolicyIds={results ? getPolicyIds(results.savedSearchDataPolicies) : undefined}
        />
      )}
    </BenchlingQuery>
  );
}

/** A modal for editing the metadata (name, description, etc.) of a Saved Search. */
export function SavedSearchSettingsModal({
  initialValues,
  title,
  onRequestSubmit,
  onRequestClose,
  onDelete,
  existingSavedSearch,
  dataPolicyIds,
  autoFocus,
  noWriteAccess,
  copy,
  loading,
}: SavedSearchSettingsModalProps & PropsFromQuery): JSX.Element {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [newCollaborators, setNewCollaborators] = useState<Array<DataPolicyCollaborator> | null>(null);
  const [newOwner, setNewOwner] = useState<Owner | null>(null);
  const [newOwnerInfo, setNewOwnerInfo] = useState<OwnerInfo | null>(null);

  if (!isAccessibleOrNull(existingSavedSearch)) {
    throw new Error('Saved search is inaccessible');
  }

  let existingOwner = existingSavedSearch?.ownerJson as Owner;
  let existingOwnerInfo = existingSavedSearch?.ownerInfoJson as OwnerInfo;
  let existingCollaborators = existingSavedSearch?.collaboratorsJson as Array<DataPolicyCollaborator>;
  const permissions = existingSavedSearch?.permissions;

  const currentUser = EntityStore.currentUser();
  if (
    dataPolicyIds &&
    existingOwner &&
    existingOwnerInfo &&
    currentUser &&
    existingOwner.id !== currentUser.id &&
    copy
  ) {
    existingCollaborators = existingCollaborators.filter((c) => c.id !== currentUser.id);
    const view = dataPolicyIds[SavedSearchPermissionLevel.VIEW];
    existingCollaborators.unshift(
      setDataPolicyId(existingOwner, {dataPolicyId: view, adminDataPolicyId: view})
    );

    existingOwner = currentUser;
    existingOwnerInfo = {type: ModelTypeConstants.USER, memberDataPolicyId: null};
  }

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onRequestSubmit({
        name,
        description,
        ownerJson: newOwner ?? existingOwner,
        ownerInfoJson: newOwnerInfo ?? existingOwnerInfo,
        collaboratorsJson: newCollaborators ?? existingCollaborators,
      });
      // This refetch is to update the apollo cache so things won't be changing the next time it's opened
      if (existingSavedSearch) {
        refetchSavedSearchSettings(existingSavedSearch.id);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!onDelete) {
      return;
    }

    setSubmitting(true);
    try {
      const deleted = await onDelete();
      if (deleted) {
        onRequestClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getPotentialOwners = (): Array<Owner> => {
    const collaborators = (newCollaborators ?? existingCollaborators).filter(
      (collaborator) =>
        (collaborator.type as string) === (ModelTypeConstants.USER as string) ||
        (collaborator.type as string) === (ModelTypeConstants.ORGANIZATION as string)
    );
    return [newOwner ?? existingOwner].concat(collaborators as Array<Owner>);
  };

  const selectOwner = (newOwnerId: string): void => {
    const oldOwner = newOwner ?? existingOwner;
    const oldOwnerInfo = newOwnerInfo ?? existingOwnerInfo;

    if (!oldOwner || !dataPolicyIds) {
      return;
    }

    if (oldOwner.id === newOwnerId) {
      return;
    }
    const owner = getPotentialOwners().find((owner) => owner.id === newOwnerId);

    // Remove new owner and add old owner to collaborators.
    const collaborators = (newCollaborators ?? existingCollaborators).filter((c) => c.id !== newOwnerId);
    const admin = dataPolicyIds[SavedSearchPermissionLevel.ADMIN];
    if (oldOwner.type === ModelTypeConstants.ORGANIZATION && oldOwnerInfo.memberDataPolicyId) {
      collaborators.unshift(
        setDataPolicyId(oldOwner, {dataPolicyId: oldOwnerInfo.memberDataPolicyId, adminDataPolicyId: admin})
      );
    } else {
      collaborators.unshift(setDataPolicyId(oldOwner, {dataPolicyId: admin, adminDataPolicyId: admin}));
    }

    setNewOwner(owner!);
    setNewOwnerInfo(getOwnerInfo(owner!, newCollaborators ?? existingCollaborators));
    setNewCollaborators(collaborators);
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      title={title}
      buttons={compact([
        {
          text: 'Cancel',
          cancel: true,
          disabled: submitting,
        },
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: submitting || name === '',
        },
        onDelete && {
          text: 'Delete',
          style: ButtonStyle.NEGATIVE,
          disabled: submitting,
          pullLeft: true,
          onClick: handleDelete,
        },
      ])}
      medium={true}
    >
      <div className={css(styles.twoFieldRow)}>
        <TextField
          label="Name"
          labelStyle={LabelStyle.LABEL}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          autoFocus={autoFocus === 'name' || autoFocus == null}
          disabled={noWriteAccess}
          dataTestAttrs={{component: 'SavedSearchSettingsModal', element: 'nameField'}}
          className={classNames('form-group', css(styles.nameField))}
        />
        {BenchlingConfig.ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH && existingOwner ? (
          // We don't support changing the owner until the search has been created.
          <div className={css(styles.ownerField)}>
            <SelectOwnerField
              ownableType={ModelTypeConstants.SAVED_SEARCH}
              isReadOnly={!existingSavedSearch?.permissions.admin}
              ownerId={newOwner ? newOwner.id : existingOwner.id}
              selectOwner={selectOwner}
              potentialOwners={getPotentialOwners()}
              helpMessage={getSelectOwnerHelpMessage(
                ModelTypeConstants.SAVED_SEARCH,
                true,
                OwnershipType.USER_AND_ORG
              )}
              collaboratorsAllowed={true}
            />
          </div>
        ) : null}
      </div>

      <TextField
        label="Description"
        labelStyle={LabelStyle.LABEL}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
        disabled={noWriteAccess}
        maxLength={1000} // Keep in sync with benchling/nav/models/saved_search::SavedSearch.MAX_DESCRIPTION_LENGTH
        dataTestAttrs={{component: 'SavedSearchSettingsModal', element: 'descriptionField'}}
      />

      {loading ? <CenteredHorizontalThrobber /> : null}

      {BenchlingConfig.ENABLE_SIGHTGLASS_SHARED_SAVED_SEARCH &&
      dataPolicyIds &&
      permissions &&
      existingOwner ? (
        <SavedSearchSharingPane
          savedSearchId={existingSavedSearch?.id}
          collaborators={newCollaborators ?? existingCollaborators}
          onChangeCollaborators={setNewCollaborators}
          permissions={permissions}
          permissionLevelToDataPolicyId={dataPolicyIds}
          owner={newOwner ?? existingOwner}
          ownerInfo={newOwnerInfo ?? existingOwnerInfo}
          onChangeOwnerInfo={setNewOwnerInfo}
          autoFocus={autoFocus === 'share'}
        />
      ) : null}
    </Modal>
  );
}

const styles = StyleSheet.create({
  twoFieldRow: {
    display: 'flex',
    gap: '1em',
  },
  nameField: {
    flex: '7 0 0',
  },
  ownerField: {
    flex: '5 0 0',
  },
});

function isAccessibleOrNull(
  savedSearch: SavedSearchSettingsModalQuery['savedSearch'] | null | undefined
): savedSearch is SavedSearchSettingsModalQuery_savedSearch_SavedSearchNode | null | undefined {
  return savedSearch == null || !isInaccessible(savedSearch);
}
